import { useState, useEffect } from 'react';
import './styles.css';
import TableControls from './controls';

export default function Table({ 
  headers,
  rows,
  csvHeaders,
  csvRows
}) {
  const [sortConfig, setSortConfig] = useState({ key: 0, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRows, setFilteredRows] = useState(rows);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredRows(rows);
    } else {
      const term = searchTerm.toLowerCase().trim();
      const filtered = rows.filter(row => 
        row[0].toString().toLowerCase().includes(term)
      );
      setFilteredRows(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, rows]);

  const sortedRows = [...filteredRows].sort((a, b) => {
    if (sortConfig.key === null) return 0;
    const aValue = a[sortConfig.key]?.toLowerCase() || '';
    const bValue = b[sortConfig.key]?.toLowerCase() || '';
    return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
  });

  const totalRows = filteredRows.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const paginatedRows = sortedRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handleSort = (index) => {
    setSortConfig(prev => ({
      key: index,
      direction: prev.key === index && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (direction) => {
    setCurrentPage(prev => Math.max(1, Math.min(totalPages, prev + direction)));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const cleanHeaderForCSV = (header) => {
    return header.replace(/<br>/g, ' - ')
                 .replace(/<[^>]*>/g, '')
                 .replace(/"/g, '""');
  };

  const generateCSV = () => {
    if (!csvHeaders.length || !csvRows.length) return '';

    const cleanHeaders = csvHeaders.map(header => cleanHeaderForCSV(header));
    
    const csvFileHeaders = cleanHeaders.map(header => `"${header}"`).join(',');
    const csvFileRows = csvRows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    return `${csvFileHeaders}\n${csvFileRows}`;
  };

  const handleDownloadCSVButtonClick = () => {
    const csv = generateCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const date = new Date();
    const dateString = date.toISOString().split('T')[0];
    const filename = `instructor_scores_${dateString}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="data-table-container">
      <div className="data-table-heading">
        <div className="data-table-search-input-container">
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={handleSearch}
            className="data-table-search-input"
          />
        </div>
        { csvHeaders && csvRows &&
          <button onClick={handleDownloadCSVButtonClick} className="button-download-scores">Download scores</button>
        }
      </div>

      <table className="data-table">
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th 
                key={index} 
                onClick={() => handleSort(index)}
                className={sortConfig.key === index ? `active ${sortConfig.direction}` : ''}
                dangerouslySetInnerHTML={{ __html: header }}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedRows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <TableControls
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={setRowsPerPage}
        currentPage={currentPage}
        onCurrentPageChange={setCurrentPage}
        totalRows={totalRows}
        onPageChange={handlePageChange}
        totalPages={totalPages}
      />
    </div>
  );
}
